import { useEffect, useEffectEvent, useState } from 'react'
import { Avatar, AvatarFallback } from '@journ/ui/avatar'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Separator } from '@journ/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@journ/ui/sheet'
import { useIntlayer } from 'react-intlayer'
import { listSubscriptions } from '../application/manage-members'
import type { ChatMember } from '../domain/chat'
import { useMemberLookup } from '../hooks/use-member-lookup'
import { chat as chatPort, notify } from '../wiring'
import { topicInitials } from './topic-display'

type ConversationMembersSheetProps = {
	topicId: string
	open: boolean
	onOpenChange: (open: boolean) => void
	onAdd: (userId: string) => Promise<unknown>
	onRemove: (userId: string) => Promise<unknown>
}

// Group membership management: lists current members and a people picker to add
// more. Reloads the member list after each mutation.
const ConversationMembersSheet = ({ topicId, open, onOpenChange, onAdd, onRemove }: ConversationMembersSheetProps) => {
	const content = useIntlayer('chat')
	const [members, setMembers] = useState<ChatMember[]>([])
	const [query, setQuery] = useState('')
	const candidates = useMemberLookup(open ? query : null)

	const reload = useEffectEvent(async () => {
		setMembers(await listSubscriptions(chatPort, topicId, notify))
	})

	useEffect(() => {
		if (open) reload()
	}, [open, topicId])

	const memberIds = new Set(members.map(m => m.id))
	const addable = candidates.filter(c => !memberIds.has(c.id))

	const handleAdd = async (userId: string) => {
		if (await onAdd(userId)) reload()
	}
	const handleRemove = async (userId: string) => {
		if (await onRemove(userId)) reload()
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>{content.members}</SheetTitle>
				</SheetHeader>
				<div className='mt-4 flex flex-col gap-1'>
					{members.map(member => (
						<div key={member.id} className='flex items-center gap-3 rounded-md px-2 py-1.5'>
							<Avatar className='h-8 w-8 rounded-md'>
								<AvatarFallback className='rounded-md text-xs'>{topicInitials(member.name)}</AvatarFallback>
							</Avatar>
							<span className='flex-1 truncate text-sm'>{member.name}</span>
							<Button variant='ghost' size='sm' onClick={() => handleRemove(member.id)}>
								{content.removeMember}
							</Button>
						</div>
					))}
				</div>
				<Separator className='my-4' />
				<p className='mb-2 px-2 text-sm font-medium'>{content.addMember}</p>
				<Input
					value={query}
					onChange={e => setQuery(e.target.value)}
					placeholder={content.searchPeople.value}
					className='mb-2'
				/>
				<ScrollArea className='h-64'>
					<div className='flex flex-col gap-0.5'>
						{addable.map(member => (
							<button
								key={member.id}
								type='button'
								onClick={() => handleAdd(member.id)}
								className='flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent'
							>
								<Avatar className='h-8 w-8 rounded-md'>
									<AvatarFallback className='rounded-md text-xs'>{topicInitials(member.name)}</AvatarFallback>
								</Avatar>
								<span className='flex-1 truncate text-sm'>{member.name}</span>
								<Icons.Add size={16} />
							</button>
						))}
					</div>
				</ScrollArea>
			</SheetContent>
		</Sheet>
	)
}

export { ConversationMembersSheet }
